import { createElement, useState, useEffect } from "react";
import { Link, router } from "@inertiajs/react";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { AdminLayout } from "../../../layouts/admin-layout";

interface Partner {
  id: number;
  name: string;
  slug: string;
  email: string;
  company_name: string | null;
  website_url: string | null;
  referral_plan: string | null;
  commission_rate: number;
  commission_duration_months: number;
  status: string;
  notes: string | null;
}

interface PartnerFormProps {
  partner: Partner | null;
  errors?: Record<string, string[]>;
}

function PartnerForm({ partner, errors }: PartnerFormProps) {
  const isEditing = partner !== null;
  const [form, setForm] = useState({
    name: partner?.name || "",
    slug: partner?.slug || "",
    email: partner?.email || "",
    company_name: partner?.company_name || "",
    website_url: partner?.website_url || "",
    referral_plan: partner?.referral_plan || "",
    commission_rate: partner?.commission_rate?.toString() || "25.0",
    commission_duration_months:
      partner?.commission_duration_months?.toString() || "3",
    status: partner?.status || "pending",
    notes: partner?.notes || "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Auto-generate slug from name (only for new partners)
  useEffect(() => {
    if (!isEditing && form.name) {
      setForm((prev) => ({
        ...prev,
        slug: form.name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, ""),
      }));
    }
  }, [form.name, isEditing]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    const payload = { partner: form };

    if (isEditing) {
      router.put(`/admin/partners/${partner.id}`, payload, {
        onFinish: () => setSubmitting(false),
      });
    } else {
      router.post("/admin/partners", payload, {
        onFinish: () => setSubmitting(false),
      });
    }
  };

  const fieldError = (field: string) => {
    if (!errors) return null;
    const messages = errors[field];
    if (!messages || messages.length === 0) return null;
    return (
      <p className="text-sm text-destructive mt-1">{messages.join(", ")}</p>
    );
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <header className="mb-8">
        <Link
          href={
            isEditing
              ? `/admin/partners/${partner.id}`
              : "/admin/partners"
          }
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          {isEditing ? "Back to Partner" : "Back to Partners"}
        </Link>
        <h1 className="text-3xl font-bold text-foreground">
          {isEditing ? "Edit Partner" : "New Partner"}
        </h1>
      </header>

      <div className="bg-card rounded-lg border border-border p-6">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-muted-foreground mb-1"
              >
                Name *
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={form.name}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {fieldError("name")}
            </div>
            <div>
              <label
                htmlFor="slug"
                className="block text-sm font-medium text-muted-foreground mb-1"
              >
                Slug *
              </label>
              <input
                id="slug"
                name="slug"
                type="text"
                required
                value={form.slug}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                pattern="[a-z0-9\-]+"
              />
              {fieldError("slug")}
            </div>
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-muted-foreground mb-1"
            >
              Email *
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              value={form.email}
              onChange={handleChange}
              className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            {fieldError("email")}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="company_name"
                className="block text-sm font-medium text-muted-foreground mb-1"
              >
                Company Name
              </label>
              <input
                id="company_name"
                name="company_name"
                type="text"
                value={form.company_name}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label
                htmlFor="website_url"
                className="block text-sm font-medium text-muted-foreground mb-1"
              >
                Website URL
              </label>
              <input
                id="website_url"
                name="website_url"
                type="url"
                value={form.website_url}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label
                htmlFor="commission_rate"
                className="block text-sm font-medium text-muted-foreground mb-1"
              >
                Commission Rate (%)
              </label>
              <input
                id="commission_rate"
                name="commission_rate"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={form.commission_rate}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label
                htmlFor="commission_duration_months"
                className="block text-sm font-medium text-muted-foreground mb-1"
              >
                Duration (months)
              </label>
              <input
                id="commission_duration_months"
                name="commission_duration_months"
                type="number"
                min="1"
                max="24"
                value={form.commission_duration_months}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label
                htmlFor="status"
                className="block text-sm font-medium text-muted-foreground mb-1"
              >
                Status
              </label>
              <select
                id="status"
                name="status"
                value={form.status}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="pending">Pending</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div>
            <label
              htmlFor="referral_plan"
              className="block text-sm font-medium text-muted-foreground mb-1"
            >
              Referral Plan
            </label>
            <textarea
              id="referral_plan"
              name="referral_plan"
              rows={3}
              value={form.referral_plan}
              onChange={handleChange}
              className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>

          <div>
            <label
              htmlFor="notes"
              className="block text-sm font-medium text-muted-foreground mb-1"
            >
              Admin Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              rows={3}
              value={form.notes}
              onChange={handleChange}
              className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {submitting
                ? "Saving..."
                : isEditing
                  ? "Update Partner"
                  : "Create Partner"}
            </button>
            <Link
              href={
                isEditing
                  ? `/admin/partners/${partner.id}`
                  : "/admin/partners"
              }
              className="px-6 py-2 text-sm font-medium rounded-md border border-border text-foreground hover:bg-muted transition-colors"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

PartnerForm.layout = (page: React.ReactNode) =>
  createElement(AdminLayout, null, page);

export default PartnerForm;
